import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    IconButton,
    Input,
    InputProps,
    Text,
    Textarea,
    TextareaProps,
    VStack,
    HStack,
    Checkbox,
    Badge,
    SimpleGrid,
    Tooltip,
    useColorModeValue
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faCheck,
    faPen,
    faInfo,
    faMusic,
    faEye,
    faImage
} from '@fortawesome/free-solid-svg-icons';
import { S3Client } from '@aws-sdk/client-s3';
import { QuizQuestion } from '@xnav/types/src/common';
import SignedImage from './signedImage';
import QuizAudioPlayer from './quizAudioPlayer';

// ---------------------------------------------------------------------------
// 1. Debounced Textarea - Immediate local 60 FPS typing with debounced commit
// ---------------------------------------------------------------------------
interface DebouncedTextareaProps extends Omit<TextareaProps, 'onChange'> {
    value: string;
    onDebouncedChange: (val: string) => void;
    debounceMs?: number;
}

export const DebouncedTextarea: React.FC<DebouncedTextareaProps> = ({
    value: externalValue,
    onDebouncedChange,
    debounceMs = 250,
    onBlur: externalOnBlur,
    ...rest
}) => {
    const [localValue, setLocalValue] = useState(externalValue || '');
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        // Only sync from outside if user is not actively typing/focused
        if (!isFocusedRef.current) {
            setLocalValue(externalValue || '');
        }
    }, [externalValue]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const next = e.target.value;
        setLocalValue(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            onDebouncedChange(next);
        }, debounceMs);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        isFocusedRef.current = false;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (localValue !== externalValue) {
            onDebouncedChange(localValue);
        }
        externalOnBlur?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        isFocusedRef.current = true;
        rest.onFocus?.(e);
    };

    return (
        <Textarea
            {...rest}
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
};

// ---------------------------------------------------------------------------
// 2. Debounced Input - Immediate local 60 FPS typing with debounced commit
// ---------------------------------------------------------------------------
interface DebouncedInputProps extends Omit<InputProps, 'onChange'> {
    value: string;
    onDebouncedChange: (val: string) => void;
    debounceMs?: number;
}

export const DebouncedInput: React.FC<DebouncedInputProps> = ({
    value: externalValue,
    onDebouncedChange,
    debounceMs = 250,
    onBlur: externalOnBlur,
    ...rest
}) => {
    const [localValue, setLocalValue] = useState(externalValue || '');
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        if (!isFocusedRef.current) {
            setLocalValue(externalValue || '');
        }
    }, [externalValue]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setLocalValue(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            onDebouncedChange(next);
        }, debounceMs);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = false;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (localValue !== externalValue) {
            onDebouncedChange(localValue);
        }
        externalOnBlur?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = true;
        rest.onFocus?.(e);
    };

    return (
        <Input
            {...rest}
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
};

// ---------------------------------------------------------------------------
// 3. QuestionCard Component
// ---------------------------------------------------------------------------
export interface QuestionCardProps {
    q: QuizQuestion;
    originalIndex: number;
    isSelected: boolean;
    onToggleSelect: (id: string) => void;
    onDelete: (index: number) => void;
    onUpdateQuestion: (index: number, field: keyof QuizQuestion, value: any) => void;
    onUpdateOption: (qIndex: number, optIndex: number, value: string) => void;
    onUpdateWrongExplanation: (qIndex: number, optIndex: number, value: string) => void;
    onImageUpload: (qIndex: number, e: React.ChangeEvent<HTMLInputElement>) => void;
    onAudioUpload: (qIndex: number, e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveImage: (qIndex: number, imgIndex: number) => void;
    onRemoveAudio: (qIndex: number) => void;
    onOpenAnnotator: (qIndex: number, imgIndex: number, url: string) => void;
    onOpenDescription: (target: { qIndex: number; imgIndex: number }) => void;
    onOpenLightbox: (url: string) => void;
    s3: S3Client;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
    q,
    originalIndex,
    isSelected,
    onToggleSelect,
    onDelete,
    onUpdateQuestion,
    onUpdateOption,
    onUpdateWrongExplanation,
    onImageUpload,
    onAudioUpload,
    onRemoveImage,
    onRemoveAudio,
    onOpenAnnotator,
    onOpenDescription,
    onOpenLightbox,
    s3
}) => {
    const cardBg = useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(26, 32, 44, 0.8)");
    const cardBorderColor = useColorModeValue("whiteAlpha.600", "whiteAlpha.200");
    const headingColor = useColorModeValue("gray.700", "white");
    const inputBorderColor = useColorModeValue("gray.100", "gray.700");
    const inputBg = useColorModeValue("gray.50", "gray.800");
    const inputFocusBg = useColorModeValue("white", "gray.900");
    const optionRowBg = useColorModeValue("gray.100", "gray.800");
    const optionFocusBg = useColorModeValue("white", "gray.700");
    const audioCardBg = useColorModeValue("teal.50/60", "teal.950/30");
    const audioCardBorder = useColorModeValue("teal.200", "teal.800");
    const audioTextColor = useColorModeValue("gray.800", "white");
    const imageContainerBg = useColorModeValue("gray.50", "gray.900");
    const imageCardBg = useColorModeValue("white", "gray.850");
    const imageCardBorder = useColorModeValue("gray.200", "gray.700");
    const activeOptionBg = useColorModeValue("green.50", "rgba(72, 187, 120, 0.15)");
    const activeOptionTextColor = useColorModeValue("green.700", "green.200");
    const wrongExplanationsFocusBg = useColorModeValue("red.50", "red.900");

    return (
        <Box
            bg={cardBg}
            backdropFilter="blur(16px)"
            border="1px solid"
            borderColor={cardBorderColor}
            borderRadius="2xl"
            shadow="lg"
            p={{ base: 4, md: 5 }}
            transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ shadow: 'xl', borderColor: "cyan.400" }}
        >
            {/* Header: Select Checkbox, Question Number, Title/Badges, Delete Button */}
            <Flex justify="space-between" align="center" mb={4}>
                <Flex align="center" gap={3}>
                    <Checkbox
                        isChecked={isSelected}
                        onChange={() => onToggleSelect(q.id)}
                        colorScheme="green"
                        size="md"
                    />
                    <Flex
                        bgGradient="linear(to-br, #38bdf8, #0ea5e9, #10b981)"
                        color="white"
                        w={8}
                        h={8}
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        fontWeight="bold"
                        fontSize="sm"
                        shadow="md"
                    >
                        {originalIndex + 1}
                    </Flex>
                    <Heading size="sm" color={headingColor}>
                        Question {originalIndex + 1}
                    </Heading>

                    {/* Question Format Badge */}
                    {q.questionType === 'survey' ? (
                        <Badge colorScheme="cyan" variant="subtle" px={2.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="bold">
                            📊 Survey / Poll
                        </Badge>
                    ) : q.questionType === 'type2' ? (
                        <Badge colorScheme="blue" variant="subtle" px={2.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="bold">
                            Type 2 (Simple Quiz)
                        </Badge>
                    ) : (
                        <Badge colorScheme="teal" variant="subtle" px={2.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="bold">
                            Type 1 (Detailed Quiz)
                        </Badge>
                    )}

                    <Badge colorScheme="gray" variant="outline" px={2} py={0.5} borderRadius="full" fontSize="3xs" display={{ base: "none", sm: "inline-block" }}>
                        {q.level || 'Level 1'}
                    </Badge>
                </Flex>

                <IconButton
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    aria-label="Delete Question"
                    colorScheme="red"
                    variant="ghost"
                    size="sm"
                    borderRadius="lg"
                    _hover={{ bg: "red.50", color: "red.600" }}
                    onClick={() => onDelete(originalIndex)}
                />
            </Flex>

            <VStack spacing={4} align="stretch">
                {/* Question Format & Level Compact Toolbar */}
                <Flex
                    direction={{ base: "column", md: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", md: "center" }}
                    gap={3}
                    px={3.5}
                    py={2.5}
                    bg={useColorModeValue("blackAlpha.50", "whiteAlpha.50")}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={useColorModeValue("blackAlpha.100", "whiteAlpha.100")}
                >
                    {/* Question Type */}
                    <Flex align="center" gap={2} wrap="wrap">
                        <Text fontSize="2xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                            Format:
                        </Text>
                        <HStack spacing={1.5}>
                            <Button
                                size="xs"
                                px={2.5}
                                py={1.5}
                                borderRadius="lg"
                                colorScheme={(q.questionType || 'type1') === 'type1' ? "teal" : "gray"}
                                variant={(q.questionType || 'type1') === 'type1' ? "solid" : "ghost"}
                                onClick={() => onUpdateQuestion(originalIndex, 'questionType', 'type1')}
                            >
                                Type 1 (Detailed)
                            </Button>
                            <Button
                                size="xs"
                                px={2.5}
                                py={1.5}
                                borderRadius="lg"
                                colorScheme={q.questionType === 'type2' ? "blue" : "gray"}
                                variant={q.questionType === 'type2' ? "solid" : "ghost"}
                                onClick={() => onUpdateQuestion(originalIndex, 'questionType', 'type2')}
                            >
                                Type 2 (Simple)
                            </Button>
                            <Button
                                size="xs"
                                px={2.5}
                                py={1.5}
                                borderRadius="lg"
                                colorScheme={q.questionType === 'survey' ? "cyan" : "gray"}
                                variant={q.questionType === 'survey' ? "solid" : "ghost"}
                                onClick={() => {
                                    onUpdateQuestion(originalIndex, 'questionType', 'survey');
                                    // In survey mode, reset correctAnswer to -1 (ungraded)
                                    if (q.correctAnswer !== -1) {
                                        onUpdateQuestion(originalIndex, 'correctAnswer', -1);
                                    }
                                }}
                            >
                                📊 Survey / Poll
                            </Button>
                        </HStack>
                    </Flex>

                    {/* Question Level */}
                    <Flex align="center" gap={2}>
                        <Text fontSize="2xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                            Level:
                        </Text>
                        <HStack spacing={1}>
                            {['Level 1', 'Level 2', 'Level 3'].map((lvl) => (
                                <Button
                                    key={lvl}
                                    size="xs"
                                    px={2}
                                    py={1.5}
                                    borderRadius="md"
                                    colorScheme={(q.level || 'Level 1') === lvl ? "cyan" : "gray"}
                                    variant={(q.level || 'Level 1') === lvl ? "solid" : "ghost"}
                                    onClick={() => onUpdateQuestion(originalIndex, 'level', lvl)}
                                >
                                    {lvl}
                                </Button>
                            ))}
                        </HStack>
                    </Flex>
                </Flex>

                {/* Question Text */}
                <FormControl>
                    <FormLabel
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        mb={1.5}
                    >
                        {q.questionType === 'survey' ? 'Survey Question / Prompt' : 'Question Text'}
                    </FormLabel>
                    <DebouncedTextarea
                        value={q.text || ''}
                        onDebouncedChange={(val) => onUpdateQuestion(originalIndex, 'text', val)}
                        placeholder={
                            q.questionType === 'survey'
                                ? "Ask a survey question (e.g. How confident are you identifying fetal CRL?)"
                                : "What is the main concept of..."
                        }
                        rows={2}
                        size="md"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor={inputBorderColor}
                        bg={inputBg}
                        _focus={{ borderColor: q.questionType === 'survey' ? "cyan.400" : "green.400", bg: inputFocusBg, shadow: "outline" }}
                        transition="all 0.2s"
                        mb={2}
                    />

                    {/* Question Media Section */}
                    <Box mt={1}>
                        <Flex justify="space-between" align="center" mb={2}>
                            <FormLabel
                                fontSize="2xs"
                                fontWeight="bold"
                                color="gray.400"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                mb={0}
                            >
                                Question Media
                            </FormLabel>
                            <HStack spacing={2}>
                                {q.images && q.images.length > 0 && (
                                    <Badge colorScheme="purple" borderRadius="full" px={2} py={0.5} fontSize="3xs" fontWeight="bold">
                                        <FontAwesomeIcon icon={faImage} style={{ marginRight: '4px' }} />
                                        {q.images.length} Image{q.images.length > 1 ? 's' : ''}
                                    </Badge>
                                )}
                                {q.audioUrl && (
                                    <Badge colorScheme="teal" borderRadius="full" px={2} py={0.5} fontSize="3xs" fontWeight="bold">
                                        <FontAwesomeIcon icon={faMusic} style={{ marginRight: '4px' }} />
                                        Audio Attached
                                    </Badge>
                                )}
                            </HStack>
                        </Flex>

                        {/* Media Action Buttons */}
                        <HStack spacing={2.5} mb={2} flexWrap="wrap">
                            <Button
                                as="label"
                                size="xs"
                                leftIcon={<FontAwesomeIcon icon={faImage} />}
                                cursor="pointer"
                                colorScheme="teal"
                                variant="outline"
                                borderRadius="lg"
                                fontWeight="semibold"
                            >
                                Upload Images / Diagrams
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => onImageUpload(originalIndex, e)}
                                />
                            </Button>

                            <Button
                                as="label"
                                size="xs"
                                leftIcon={<FontAwesomeIcon icon={faMusic} />}
                                cursor="pointer"
                                colorScheme="cyan"
                                variant="outline"
                                borderRadius="lg"
                                fontWeight="semibold"
                            >
                                {q.audioUrl ? "Replace Audio Clip" : "Upload Audio (Doppler/Sounds)"}
                                <input
                                    type="file"
                                    hidden
                                    accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg"
                                    onChange={(e) => onAudioUpload(originalIndex, e)}
                                />
                            </Button>
                        </HStack>

                        {/* Attached Images Grid */}
                        {q.images && q.images.length > 0 && (
                            <Box mb={3}>
                                <Text fontSize="3xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                    Attached Medical Images ({q.images.length})
                                </Text>
                                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                                    {q.images.map((img, imgIndex) => (
                                        <Box
                                            key={img.url || imgIndex}
                                            position="relative"
                                            borderRadius="xl"
                                            overflow="hidden"
                                            border="1px solid"
                                            borderColor={imageCardBorder}
                                            bg={imageCardBg}
                                            p={2}
                                            role="group"
                                            transition="all 0.2s"
                                            _hover={{ shadow: "md", borderColor: "cyan.400" }}
                                            shadow="xs"
                                        >
                                            {/* Hover Action Bar */}
                                            <Flex
                                                justify="space-between"
                                                opacity={0}
                                                _groupHover={{ opacity: 1 }}
                                                transition="opacity 0.2s"
                                                bg="rgba(15, 23, 42, 0.85)"
                                                backdropFilter="blur(8px)"
                                                position="absolute"
                                                top={0}
                                                left={0}
                                                right={0}
                                                p={1.5}
                                                zIndex={3}
                                                borderBottom="1px solid"
                                                borderColor="whiteAlpha.200"
                                            >
                                                <HStack spacing={1}>
                                                    <Tooltip label="Draw Annotations / Clinical Markups" placement="top" hasArrow>
                                                        <IconButton
                                                            size="xs"
                                                            aria-label="Draw Annotations"
                                                            icon={<FontAwesomeIcon icon={faPen} />}
                                                            colorScheme="teal"
                                                            borderRadius="md"
                                                            onClick={() => onOpenAnnotator(originalIndex, imgIndex, (img as any).originalUrl || img.url)}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Edit Image Description" placement="top" hasArrow>
                                                        <IconButton
                                                            size="xs"
                                                            aria-label="Edit Image Description"
                                                            icon={<FontAwesomeIcon icon={faInfo} />}
                                                            colorScheme="cyan"
                                                            borderRadius="md"
                                                            onClick={() => onOpenDescription({ qIndex: originalIndex, imgIndex })}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Zoom Lightbox" placement="top" hasArrow>
                                                        <IconButton
                                                            size="xs"
                                                            aria-label="Zoom Image"
                                                            icon={<FontAwesomeIcon icon={faEye} />}
                                                            colorScheme="blue"
                                                            borderRadius="md"
                                                            onClick={() => onOpenLightbox(img.url)}
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                                <Tooltip label="Delete Image" placement="top" hasArrow>
                                                    <IconButton
                                                        size="xs"
                                                        aria-label="Delete"
                                                        icon={<FontAwesomeIcon icon={faTrash} />}
                                                        colorScheme="red"
                                                        borderRadius="md"
                                                        onClick={() => onRemoveImage(originalIndex, imgIndex)}
                                                    />
                                                </Tooltip>
                                            </Flex>

                                            {/* Image Container */}
                                            <Box
                                                h="120px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                bg={imageContainerBg}
                                                borderRadius="lg"
                                                overflow="hidden"
                                                cursor="pointer"
                                                onClick={() => onOpenLightbox(img.url)}
                                            >
                                                <SignedImage s3Client={s3} src={img.url} alt="Question media" objectFit="contain" maxH="115px" w="100%" />
                                            </Box>

                                            {/* Image Caption & Status */}
                                            <Flex justify="space-between" align="center" mt={1.5} px={1}>
                                                <Text fontSize="3xs" color="gray.400" noOfLines={1} title={img.description || "Question diagram"}>
                                                    {img.description ? `📝 ${img.description}` : `Image #${imgIndex + 1}`}
                                                </Text>
                                                {img.annotations || (img as any).markerState ? (
                                                    <Badge colorScheme="green" fontSize="3xs" borderRadius="full" px={1.5}>
                                                        Annotated
                                                    </Badge>
                                                ) : null}
                                            </Flex>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        )}

                        {/* Attached Audio Player Card */}
                        {q.audioUrl && (
                            <Box
                                p={3}
                                bg={audioCardBg}
                                borderRadius="xl"
                                border="1px solid"
                                borderColor={audioCardBorder}
                                shadow="xs"
                                mb={2}
                            >
                                <Flex justify="space-between" align="center" mb={1.5}>
                                    <HStack spacing={2}>
                                        <Flex w="26px" h="26px" borderRadius="lg" bg="teal.500" color="white" align="center" justify="center" fontSize="xs">
                                            <FontAwesomeIcon icon={faMusic} />
                                        </Flex>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="bold" color={audioTextColor} noOfLines={1}>
                                                {q.audioTitle || "Clinical Audio Recording / Doppler Clip"}
                                            </Text>
                                            <Text fontSize="3xs" color="teal.500" fontWeight="bold">
                                                Interactive Audio Examination
                                            </Text>
                                        </Box>
                                    </HStack>
                                    <Tooltip label="Remove Audio" placement="top" hasArrow>
                                        <IconButton
                                            size="xs"
                                            aria-label="Remove Audio"
                                            icon={<FontAwesomeIcon icon={faTrash} />}
                                            colorScheme="red"
                                            variant="ghost"
                                            borderRadius="md"
                                            onClick={() => onRemoveAudio(originalIndex)}
                                        />
                                    </Tooltip>
                                </Flex>
                                <QuizAudioPlayer s3Client={s3} audioUrl={q.audioUrl} />
                            </Box>
                        )}
                    </Box>
                </FormControl>

                {/* Answer Options / Survey Choices */}
                <FormControl>
                    <Flex justify="space-between" align="center" mb={2}>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={0}>
                            {q.questionType === 'survey' ? 'Survey Choices' : 'Answer Options'}
                        </FormLabel>
                        {q.questionType === 'survey' ? (
                            <Badge colorScheme="cyan" fontSize="3xs" px={2} py={0.5} borderRadius="full">
                                📊 Ungraded Survey • No correct answer needed
                            </Badge>
                        ) : (
                            <Text fontSize="3xs" color="gray.400">
                                Click circle icon to mark the correct answer
                            </Text>
                        )}
                    </Flex>
                    <Flex direction="column" gap={2}>
                        {q.options.map((opt, optIndex) => {
                            const isCorrect = q.questionType !== 'survey' && q.correctAnswer === optIndex;
                            const isSurvey = q.questionType === 'survey';
                            const letter = String.fromCharCode(65 + optIndex);

                            return (
                                <Flex
                                    key={optIndex}
                                    align="center"
                                    gap={2.5}
                                    p={1.5}
                                    px={2.5}
                                    borderRadius="xl"
                                    bg={isCorrect ? activeOptionBg : isSurvey ? useColorModeValue("cyan.50/20", "whiteAlpha.50") : "transparent"}
                                    border="1.5px solid"
                                    borderColor={isCorrect ? "green.400" : isSurvey ? useColorModeValue("cyan.200", "whiteAlpha.100") : "transparent"}
                                    transition="all 0.2s"
                                >
                                    {isSurvey ? (
                                        /* Survey Choice Letter Badge (No correct answer radio) */
                                        <Flex
                                            w="30px"
                                            h="30px"
                                            borderRadius="lg"
                                            bg={useColorModeValue("cyan.50", "rgba(14, 165, 233, 0.15)")}
                                            color={useColorModeValue("cyan.700", "cyan.300")}
                                            fontWeight="bold"
                                            fontSize="xs"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                            border="1px solid"
                                            borderColor={useColorModeValue("cyan.200", "rgba(14, 165, 233, 0.35)")}
                                        >
                                            {letter}
                                        </Flex>
                                    ) : (
                                        /* Graded Quiz Correct Selector Button */
                                        <Tooltip label={isCorrect ? "Correct Answer" : "Click to mark as correct answer"} placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Mark Correct"
                                                icon={<FontAwesomeIcon icon={faCheck} />}
                                                colorScheme={isCorrect ? "green" : "gray"}
                                                variant={isCorrect ? "solid" : "outline"}
                                                borderRadius="full"
                                                onClick={() => onUpdateQuestion(originalIndex, 'correctAnswer', optIndex)}
                                                size="xs"
                                                w="28px"
                                                h="28px"
                                                shadow={isCorrect ? "sm" : "none"}
                                            />
                                        </Tooltip>
                                    )}

                                    <Flex direction="column" w="100%" gap={1}>
                                        <DebouncedInput
                                            size="md"
                                            value={opt || ''}
                                            onDebouncedChange={(val) => onUpdateOption(originalIndex, optIndex, val)}
                                            borderRadius="lg"
                                            border="none"
                                            bg={optionRowBg}
                                            _focus={{ bg: optionFocusBg, shadow: "sm" }}
                                            placeholder={
                                                isSurvey
                                                    ? `Choice ${letter} (e.g. Strongly Agree, Positive, etc.)`
                                                    : `Option ${letter}`
                                            }
                                            fontWeight={isCorrect ? "bold" : "normal"}
                                            color={isCorrect ? activeOptionTextColor : "inherit"}
                                        />
                                        {!isSurvey && !isCorrect && (q.questionType || 'type1') === 'type1' && (
                                            <DebouncedInput
                                                size="xs"
                                                value={(q.wrongExplanations && q.wrongExplanations[optIndex]) || ""}
                                                onDebouncedChange={(val) => onUpdateWrongExplanation(originalIndex, optIndex, val)}
                                                placeholder={`Why is option ${letter} wrong?`}
                                                variant="filled"
                                                borderRadius="md"
                                                _focus={{ bg: wrongExplanationsFocusBg }}
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                            );
                        })}
                    </Flex>
                </FormControl>

                {/* Explanation (Hidden for Survey Questions) */}
                {q.questionType !== 'survey' && (
                    <FormControl>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>
                            Explanation (Optional)
                        </FormLabel>
                        <DebouncedTextarea
                            value={q.explanation || ''}
                            onDebouncedChange={(val) => onUpdateQuestion(originalIndex, 'explanation', val)}
                            placeholder="Explain why the correct answer is right..."
                            rows={2}
                            size="sm"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor={inputBorderColor}
                            bg={inputBg}
                            _focus={{ borderColor: "green.400", bg: inputFocusBg }}
                            transition="all 0.2s"
                        />
                    </FormControl>
                )}
            </VStack>
        </Box>
    );
};

export default React.memo(QuestionCard, (prev, next) => {
    return (
        prev.q === next.q &&
        prev.originalIndex === next.originalIndex &&
        prev.isSelected === next.isSelected &&
        prev.s3 === next.s3
    );
});
